import React, {useCallback, useEffect, useRef} from 'react';
import {Image} from '@udemy-v2/react-core-components';
import classNames from 'classnames';
import {Icon} from '@udemy-v2/icons';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {tokens} from '@udemy-v2/styles';
import {noop} from '@udemy-v2/utils';
import {useI18n, Duration} from '@udemy-v2/i18n';
import {
    useIsCurrentPreview,
    useSetSelectedPreviewLectureId,
    useIsUsingPromoVideoFallback,
    usePreviewableLectures,
    PROMO_VIDEO_ID,
} from './context/course-preview-video-context';
import dynamic from 'next/dynamic';
import {PreviewableLecture} from '@/types/lecture';
import {useClpCourse} from '../../context/clp-course-provider.react-component';
import {useVideoPlayerContext} from '../video-player/video-player-context';
import {publishEvent} from '@udemy-v2/event-tracking';
import {PreviewVideoPlayEvent} from '../../events/events';
import {PreviewVideoViewEvent} from '@/features/course-landing-page/components/course-preview/events';

import styles from './course-preview.module.scss';

// Dynamically import VideoPlayer to run only on the client (avoids `document` reference during SSR).
const VideoPlayer = dynamic(
    () =>
        import(
            '@/features/course-landing-page/components/video-player/video-player.react-component'
        ),
    {
        ssr: true,
    },
);

const THUMBNAIL_WIDTH = 640;
const THUMBNAIL_HEIGHT = 480;

interface PreviewRowProps {
    /**
     * Video preview to play
     */
    previewableLecture: PreviewableLecture;
    /**
     * Called when the play button is clicked
     */
    onPlay?(): void;
    /**
     * Called when this preview is selected to publish tracking event
     */
    onPreviewSelected?(lectureId: string): void;
}

export const PreviewRow = ({
    previewableLecture,
    onPlay: play = noop,
    onPreviewSelected,
}: PreviewRowProps) => {
    const isMobile = useMatchMedia(`(max-width: ${tokens['viewport-sm-max']})`);
    const {id, title, thumbnail, durationInSeconds} = previewableLecture;
    // For instructor promo videos, we set duration to 0 since we don't have the duration on-hand
    const showPreviewVideoDuration = !isMobile && durationInSeconds > 0;
    const isCurrentPreview = useIsCurrentPreview(id);
    const setSelectedPreviewLectureId = useSetSelectedPreviewLectureId();

    const handlePreviewSelection = (): void => {
        setSelectedPreviewLectureId(id, () => {
            onPreviewSelected?.(id);
        });
        play();
    };

    return (
        <button
            className={classNames('ud-custom-focus-visible', styles['preview-row'], {
                [styles['current-preview-row']]: isCurrentPreview,
            })}
            onClick={handlePreviewSelection}
        >
            <div className={styles['preview-thumbnail-wrapper']}>
                <Image
                    src={thumbnail}
                    alt=""
                    width={THUMBNAIL_WIDTH}
                    height={THUMBNAIL_HEIGHT}
                    className={styles['preview-thumbnail']}
                />
            </div>
            {isCurrentPreview && (
                <Icon glyph="play" label={false} color="inherit" className={styles.icon} />
            )}
            <div
                className={classNames(
                    'ud-heading-sm',
                    'ud-focus-visible-target',
                    styles['preview-row-title'],
                )}
            >
                {title}
            </div>
            {showPreviewVideoDuration && (
                <div className="ud-heading-xs">
                    <Duration
                        numSeconds={durationInSeconds}
                        presentationStyle={Duration.STYLE.TIMESTAMP}
                    />
                </div>
            )}
        </button>
    );
};

export interface CoursePreviewProps {
    /**
     * UI region for event tracking
     */
    uiRegion?: string;
    /**
     * Selected lecture ID to start with
     */
    selectedLectureId?: string;
}

export const CoursePreview = ({uiRegion, selectedLectureId}: CoursePreviewProps) => {
    const {gettext} = useI18n();
    const course = useClpCourse();
    const previewableLectures = usePreviewableLectures();
    const {asset} = useVideoPlayerContext();
    const setSelectedPreviewLectureId = useSetSelectedPreviewLectureId();
    const isUsingPromoVideoFallback = useIsUsingPromoVideoFallback();
    const hasInitialized = useRef(false);

    // Memoized helper function to publish preview event
    const publishPreviewEvent = useCallback(
        (lectureId: string) => {
            if (!previewableLectures || !uiRegion) return;

            const targetLecture = previewableLectures.find((lecture) => lecture.id === lectureId);

            if (targetLecture) {
                // Safely convert string IDs to numbers, fallback to -1 if conversion fails
                const courseIdNum = Number(course.id) || 0;
                // For promo video, use -1 as previewId since it's not a real lecture ID
                const previewIdNum =
                    targetLecture.id === PROMO_VIDEO_ID ? -1 : Number(targetLecture.id) || -1;

                publishEvent(
                    new PreviewVideoViewEvent({
                        courseId: courseIdNum,
                        buyableTrackingId: course.id, // Use course.id as tracking ID like other components
                        previewId: previewIdNum,
                        uiRegion,
                    }),
                );
            }
        },
        [previewableLectures, course.id, uiRegion],
    );

    // Handle initial lecture selection and event publishing
    useEffect(() => {
        if (previewableLectures && previewableLectures.length > 0 && !hasInitialized.current) {
            const targetLectureId = selectedLectureId || previewableLectures[0].id;

            setSelectedPreviewLectureId(targetLectureId, () =>
                publishPreviewEvent(targetLectureId),
            );

            hasInitialized.current = true;
        }
    }, [selectedLectureId, previewableLectures, setSelectedPreviewLectureId, publishPreviewEvent]);

    // Handle promo video fallback case - fire event once when using promo video
    useEffect(() => {
        if (isUsingPromoVideoFallback && uiRegion && !hasInitialized.current) {
            const courseIdNum = Number(course.id) || 0;

            publishEvent(
                new PreviewVideoViewEvent({
                    courseId: courseIdNum,
                    buyableTrackingId: course.id,
                    previewId: -1, // Use -1 for promo video fallback
                    uiRegion,
                }),
            );

            hasInitialized.current = true;
        }
    }, [isUsingPromoVideoFallback, course.id, uiRegion]);

    // Create callback for play/pause events specific to preview videos
    const handlePlayPauseToggled = useCallback(
        (isPlaying: boolean, currentTime: number) => {
            // Only track play events, not pause events
            if (isPlaying && asset) {
                // For promo video (asset.id === PROMO_VIDEO_ID), use -1 as previewId
                const previewId =
                    asset.id === PROMO_VIDEO_ID
                        ? -1
                        : asset?.id !== undefined && asset.id !== '' && !isNaN(Number(asset.id))
                          ? Number(asset.id)
                          : -1;

                publishEvent(
                    new PreviewVideoPlayEvent({
                        courseId: Number(course.id),
                        buyableTrackingId: course.id,
                        previewId,
                        offset: Math.floor(currentTime),
                        nonInteraction: false,
                        uiRegion,
                    }),
                );
            }
        },
        [course.id, asset, uiRegion],
    );

    return (
        <div className={styles.previews}>
            <h2 className={styles['course-preview-modal-title']}>
                <span className={classNames('ud-heading-sm', styles['preview-intro'])}>
                    {gettext('Course Preview')}
                </span>
                <span className="ud-heading-lg">{course.title}</span>
            </h2>
            <VideoPlayer onPlayPauseToggled={handlePlayPauseToggled} />
            {previewableLectures?.length && (
                <>
                    <div className={classNames('ud-heading-md', styles['separation-text'])}>
                        {gettext('Free Sample Videos:')}
                    </div>
                    <div className={styles['preview-rows']}>
                        {(previewableLectures ?? []).map(
                            (previewableLecture: PreviewableLecture) => (
                                <PreviewRow
                                    previewableLecture={previewableLecture}
                                    key={previewableLecture.id}
                                    onPreviewSelected={publishPreviewEvent}
                                />
                            ),
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
